import MapView from "@arcgis/core/views/MapView";
import esriConfig from "@arcgis/core/config";
import Basemap from "@arcgis/core/Basemap";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { LayerType } from "../../types/layer-type";
import {
  checkInfrastructureAvailability,
  deleteBufferDistance,
  drawBufferDistance,
  drawVertices,
  getLayers,
  initializeMap,
  polylineOff,
  polylineOn,
  removeTerritoryObject,
  setObjectVisibility,
  takeScreenshotAsyncAction,
  zoomIn,
  zoomOut,
} from "../actions/map";
import {
  MapSection,
  masterCreateStudyTypes,
  setLayersVisible,
  setLoading,
  setMapComponentReady,
  startDrawingTools,
  stopDrawingTools,
  validArcgisLayerTypes,
} from "../reducers/map";
import createViewer, {
  applyHoverEffect,
  CreateHybridBaseMap,
  CreateTerrainBaseMap,
  createMultipleScreenshots,
  getPopUpTemplate,
  waitForViewerReady,
  AddLegend,
  changeBaseMap,
  updateSnappingSources,
  finishDrawingProcess,
  stopMeasuring,
  startDrawingProcess,
} from "../../utils/helpers-for-2d-map";
import { LayersDataTypes } from "../../types/layers-data-types";
import { Middleware } from "redux";
import { RootState } from "../store";
import {
  ArcgisHybridLayer,
  ArcgisTerrainLayer,
  Layer,
} from "../../interfaces/layer";
import {
  resetAllAnalysisResults,
  resetCreatedObject,
  setBufferInfrastructureResults,
  setCreatedObjectInfrastructureResults,
  setExternalServicesAvailability,
  setGeometries,
  setLogisticsResults,
  setPhaseTerritoryGraphic,
  setPhaseTerritoryVisible,
  setSelectedFeature,
  setViewer,
} from "../reducers/master-create-study";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import Graphic from "@arcgis/core/Graphic";
import Polygon from "@arcgis/core/geometry/Polygon";
import { getGeometryForContour } from "../actions/master-plan-modification";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import { v4 as uuidv4 } from "uuid";
import {
  ConvertPolygonsToCoordinatesTable,
  ConvertToPolygons,
  isPolygon,
} from "../../utils/master-plan-helper";
import {
  applyEditToPlotGeometry,
  buildVertexIndexMap,
  clearLastSelectedGraphic,
  deleteCreatedObjects,
  DrawVertices,
  DrawVerticesLabels,
  EditGraphics,
  EndCreateGraphic,
  getGeometry,
  handleDeleteKey,
  handleFeatureClickStyle,
  handleViewerClick,
  SimpleFillSymbolProperties,
} from "../../utils/master-map-helper";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Geometry from "@arcgis/core/geometry/Geometry";
import {
  checkLayerHasFeatures,
  getDistanceAndDirectionToInfrastructure,
  getInfrastructureAvailability,
  getLogisticsDistances,
} from "../../utils/master-create-study-helper";
import * as projection from "@arcgis/core/geometry/projection";
import { BufferInfrastructureResults } from "../../interfaces/master-create-study";
import Polyline from "@arcgis/core/geometry/Polyline";

export const masterStudyMiddleware = (
  section: MapSection,
): Middleware<{}, RootState> => {
  return (store) => {
    let viewer: MapView | null = null;
    let baseMap: Basemap;
    let terrainBaseMap: Basemap;
    let tempLayer: GraphicsLayer;
    let phaseTerritoryLayer: GraphicsLayer;
    let bufferLayer: GraphicsLayer;
    let verticesTempLayer: GraphicsLayer;
    let verticesLabelTempLayer: GraphicsLayer;
    let polygonsLayer: GraphicsLayer;
    let linesLayer: GraphicsLayer;
    let sketchViewModel: SketchViewModel;
    const customSimpleFillStyleClicked: {
      "simple-fill": SimpleFillSymbolProperties;
    } = {
      "simple-fill": {
        style: "backward-diagonal",
        color: "#e59e6d",
      },
    };
    const { dispatch } = store;

    function AddLayers(viewer: MapView) {
      const { layers, visibleLayersId } = store.getState().map;

      const baseMapLayer = layers.find(
        (l) =>
          l.dataType === LayersDataTypes.BaseMap &&
          visibleLayersId.includes(l.id),
      );
      if (
        baseMapLayer &&
        (baseMapLayer !== ArcgisTerrainLayer ||
          baseMapLayer !== ArcgisHybridLayer)
      ) {
        changeBaseMap(baseMapLayer, viewer, getBaseMap);
      }
      layers
        .filter(
          (layer) =>
            masterCreateStudyTypes.includes(layer.dataType) &&
            (layer.type === LayerType.ArcgisFeatureServer ||
              layer.type === LayerType.ArcgisMapServer ||
              layer.type === LayerType.GeoJSON),
        )
        .forEach(async (layer) => {
          if (layer.dataType === LayersDataTypes.BaseMap) return;
          const alwaysVisibleLayer =
            layer.dataType === LayersDataTypes.OmanBorder;
          const visible =
            visibleLayersId.includes(layer.id) ||
            (layer.parentId !== undefined &&
              visibleLayersId.includes(layer.parentId)) ||
            alwaysVisibleLayer;

          const layerProperties = {
            title: layer.label,
            id: layer.name,
            url: layer.url,
            visible: visible,
            definitionExpression: layer.style2D?.query?.replace(
              " and LandUse <> 3",
              "",
            ),
            editingEnabled: layer.canEdit ?? false,
            outFields: ["*"],
          } as any;

          if (layer.style2D?.default) {
            layerProperties.renderer = layer.style2D?.default;
          }

          if (layer.type === LayerType.ArcgisFeatureServer) {
            const arcgisLayer = new FeatureLayer(layerProperties);

            let hoverStyle = layer.style2D?.hover;
            const isHover = layer.style2D?.label?.type === "hover";
            const mode = (hoverStyle ? "style" : "") + (isHover ? "popup" : "");

            if (mode) {
              if (isHover && layer.style2D?.label) {
                arcgisLayer.popupTemplate = new PopupTemplate(
                  getPopUpTemplate(layer.style2D?.label),
                );
              }
              applyHoverEffect(
                viewer,
                arcgisLayer,
                mode,
                hoverStyle,
                layer.style2D?.fieldName,
              );
            }
            viewer.map.add(arcgisLayer);
          }

          if (layer.type === LayerType.ArcgisMapServer) {
            const mapImageLayer = new MapImageLayer({
              title: layer.label,
              id: layer.name,
              url: layer.url,
              visible: visible,
            });
            if (layer.style2D?.subLayers) {
              mapImageLayer.load().then(() => {
                mapImageLayer.allSublayers.forEach((subLayer) => {
                  subLayer.visible = layer.style2D!.subLayers!.includes(
                    subLayer.id,
                  );
                });
              });
            }
            viewer.map.add(mapImageLayer);
          }
          if (layer.type === LayerType.GeoJSON) {
            const geoJSONLayer = new GeoJSONLayer(layerProperties);

            let hoverStyle = layer.style2D?.hover;
            const isHover = layer.style2D?.label?.type === "hover";
            const mode = (hoverStyle ? "style" : "") + (isHover ? "popup" : "");
            if (mode) {
              if (isHover && layer.style2D?.label) {
                geoJSONLayer.popupTemplate = new PopupTemplate(
                  getPopUpTemplate(layer.style2D?.label),
                );
              }
              applyHoverEffect(
                viewer,
                geoJSONLayer,
                mode,
                hoverStyle,
                layer.style2D?.fieldName,
              );
            }
            if (layer.dataType === LayersDataTypes.Reports) {
              AddLegend(viewer, geoJSONLayer);
            }
            viewer.map.add(geoJSONLayer);
          }
        });
    }

    function getBaseMap(layer: Layer) {
      return layer === ArcgisHybridLayer
        ? baseMap
        : layer === ArcgisTerrainLayer
          ? terrainBaseMap
          : null;
    }

    function updateSnappingWrapper() {
      const ignoredLayers = [
        bufferLayer,
        verticesTempLayer,
        verticesLabelTempLayer,
      ];

      updateSnappingSources(viewer, sketchViewModel, ignoredLayers);
    }

    function DrawTempGeometry(
      enableClearSelectedGraphic: boolean = false,
      drawVertices: boolean = true,
    ) {
      if (sketchViewModel.state === "active") {
        sketchViewModel.complete();
      }

      const polygonsAndLinesIds = [
        ...polygonsLayer.graphics,
        ...linesLayer.graphics,
      ].map((g) => g.attributes.uuid);

      verticesTempLayer.removeMany(
        verticesTempLayer.graphics
          .filter((g) => polygonsAndLinesIds.includes(g.attributes.uuid))
          .toArray(),
      );
      verticesLabelTempLayer.removeMany(
        verticesLabelTempLayer.graphics
          .filter((g) => polygonsAndLinesIds.includes(g.attributes.uuid))
          .toArray(),
      );
      polygonsLayer.removeAll();
      linesLayer.removeAll();

      let lastGraphic: Graphic | null = null;

      store
        .getState()
        .studyCreation.createdObject.filter((plot) => plot.newGeometry)
        .forEach((plot) => {
          if (!plot.newGeometry) return;

          const spatialReference = plot.newGeometry.spatialReference;
          const allRings = plot.newGeometry.rings || [];

          let coordIndexMap: Map<string, number> = new Map();

          const closedRingsWithIndex: { ring: number[][]; index: number }[] =
            [];
          const openPaths: { path: number[][]; index: number }[] = [];

          coordIndexMap = buildVertexIndexMap(allRings);

          const uuid = uuidv4();

          allRings.forEach((ring, index) => {
            if (isPolygon(ring)) {
              closedRingsWithIndex.push({ ring, index });
            } else {
              openPaths.push({ path: ring, index });
            }
          });

          if (closedRingsWithIndex.length) {
            const closedRings = closedRingsWithIndex.map((i) => i.ring);
            const closedRingIndices = closedRingsWithIndex.map((i) => i.index);

            const polygon = new Polygon({
              rings: closedRings,
              spatialReference,
            });

            const polygonGraphic = new Graphic({
              geometry: polygon,
              symbol: new SimpleFillSymbol({
                style: "backward-diagonal",
                color: "#2e1a47",
                outline: {
                  color: "#2E1A47CC",
                  width: 3,
                },
              }),
              attributes: {
                objectId: plot.objectId,
                uuid,
                spatialReference,
                ringIndices: closedRingIndices,
              },
            });

            polygonsLayer.add(polygonGraphic);
            lastGraphic = polygonGraphic;

            if (drawVertices) {
              DrawVertices(polygon, uuid, false, verticesTempLayer);
              DrawVerticesLabels(
                polygon,
                uuid,
                false,
                verticesLabelTempLayer,
                coordIndexMap,
              );
            }
          }

          openPaths.forEach(({ path, index }) => {
            const polyline = new Polyline({ paths: [path], spatialReference });

            const lineGraphic = new Graphic({
              geometry: polyline,
              symbol: new SimpleLineSymbol({ color: "#FFFFFF", width: 2 }),
              attributes: {
                objectId: plot.objectId,
                uuid,
                spatialReference,
                ringIndex: index,
              },
            });

            linesLayer.add(lineGraphic);
            lastGraphic = lineGraphic;

            if (drawVertices) {
              DrawVertices(polyline, uuid, false, verticesTempLayer);
              DrawVerticesLabels(
                polyline,
                uuid,
                false,
                verticesLabelTempLayer,
                coordIndexMap,
              );
            }
          });
        });

      if (lastGraphic && !enableClearSelectedGraphic) {
        const graphic = lastGraphic as Graphic;
        const targetLayer =
          graphic.geometry.type === "polyline" ? linesLayer : polygonsLayer;
        handleFeatureClickStyle(
          graphic,
          store,
          targetLayer,
          viewer,
          sketchViewModel,
          undefined,
          undefined,
          customSimpleFillStyleClicked,
        );
      } else {
        clearLastSelectedGraphic([
          tempLayer,
          phaseTerritoryLayer,
          polygonsLayer,
          linesLayer,
        ]);
      }
    }

    return (next) => async (action) => {
      if (initializeMap.match(action)) {
        if (action.payload.section === section) {
          if (viewer === null) {
            await next(action);
            baseMap = CreateHybridBaseMap();
            terrainBaseMap = CreateTerrainBaseMap();
            viewer = createViewer(
              store,
              AddLayers,
              `${section}MapContainer`,
              baseMap,
            );
            dispatch(setViewer(viewer));

            bufferLayer = new GraphicsLayer();
            tempLayer = new GraphicsLayer();
            verticesTempLayer = new GraphicsLayer();
            verticesLabelTempLayer = new GraphicsLayer();
            phaseTerritoryLayer = new GraphicsLayer();
            polygonsLayer = new GraphicsLayer();
            linesLayer = new GraphicsLayer();

            viewer.map.add(bufferLayer);
            viewer.map.add(polygonsLayer);
            viewer.map.add(linesLayer);
            viewer.map.add(tempLayer);
            viewer.map.add(phaseTerritoryLayer);
            viewer.map.add(verticesTempLayer);
            viewer.map.add(verticesLabelTempLayer);

            sketchViewModel = new SketchViewModel({
              updateOnGraphicClick: false,
              defaultUpdateOptions: {
                tool: "reshape",
                toggleToolOnClick: false,
                reshapeOptions: {
                  shapeOperation: "none",
                },
              },
              layer: tempLayer,
              view: viewer,
              snappingOptions: {
                enabled: true,
                selfEnabled: false,
                featureSources: [],
              },
              polygonSymbol: new SimpleFillSymbol({
                color: "#FAFAFA33",
                outline: {
                  color: "#2E1A47CC",
                  width: 3,
                },
              }),
            });
            sketchViewModel.on("update", (event) => {
              const isPhaseObject = event.graphics.some((graphic) =>
                phaseTerritoryLayer.graphics.includes(graphic),
              );

              if (isPhaseObject) {
                sketchViewModel.cancel();
                return;
              }

              if (event.toolEventInfo) {
                (async () => {
                  const mergedGeometryGraphic = await applyEditToPlotGeometry(
                    event.graphics[0],
                    store.getState().studyCreation.createdObject,
                  );
                  if (mergedGeometryGraphic) {
                    const rings = (mergedGeometryGraphic!.geometry as Polygon)
                      .rings;
                    const coordIndexMap = buildVertexIndexMap(rings);
                    EditGraphics(
                      [mergedGeometryGraphic],
                      dispatch,
                      verticesLabelTempLayer,
                      undefined,
                      coordIndexMap,
                    );
                  }
                })();
              } else {
                if (event.state === "start" || event.state === "complete")
                  event.graphics.forEach((graphic) => {
                    DrawVertices(
                      graphic.geometry as Polygon,
                      graphic.attributes.uuid,
                      event.state === "start",
                      verticesTempLayer,
                    );
                  });
              }
            });
            sketchViewModel.on("create", (event) => {
              if (event.state === "cancel" && !event.graphic?.attributes) {
                return;
              }
              if (event.state === "start") {
                event.graphic.attributes = { uuid: uuidv4() };
              }
              if (event.state === "complete") {
                DrawVertices(
                  event.graphic.geometry as Polygon,
                  event.graphic.attributes.uuid,
                  false,
                  verticesTempLayer,
                );
                DrawVerticesLabels(
                  event.graphic.geometry as Polygon,
                  event.graphic.attributes.uuid,
                  false,
                  verticesLabelTempLayer,
                );

                handleFeatureClickStyle(
                  event.graphic,
                  store,
                  tempLayer,
                  viewer,
                  sketchViewModel,
                );

                dispatch(setSelectedFeature(event.graphic));
              }
            });

            viewer!.on("click", async (event) => {
              const response = await viewer!.hitTest(event);

              const clickedGraphicResult = response.results[0];

              const customStyle =
                clickedGraphicResult &&
                "graphic" in clickedGraphicResult &&
                polygonsLayer.graphics.includes(clickedGraphicResult.graphic)
                  ? customSimpleFillStyleClicked
                  : undefined;

              handleViewerClick(
                event,
                store,
                sketchViewModel,
                viewer,
                [tempLayer, phaseTerritoryLayer, polygonsLayer, linesLayer],
                tempLayer,
                undefined,
                customStyle,
              );

              if (event.native?.button !== 0) return;

              if (clickedGraphicResult && "graphic" in clickedGraphicResult) {
                dispatch(setSelectedFeature(clickedGraphicResult.graphic));
              } else {
                clearLastSelectedGraphic([
                  tempLayer,
                  phaseTerritoryLayer,
                  polygonsLayer,
                  linesLayer,
                ]);
              }
            });

            viewer.container.addEventListener("keydown", (event) => {
              const { phaseTerritory } = store.getState().studyCreation;

              const createdObjects =
                store.getState().studyCreation.createdObject;
              const result = handleDeleteKey(
                event,
                store,
                [tempLayer, phaseTerritoryLayer, polygonsLayer, linesLayer],
                verticesTempLayer,
                createdObjects,
                () =>
                  store.dispatch(
                    setGeometries({
                      newGeometry: ConvertToPolygons([]),
                      stringGeometry: ConvertPolygonsToCoordinatesTable([]),
                    }),
                  ),
                verticesLabelTempLayer,
                false,
                sketchViewModel,
              );

              if (result) {
                const graphic = "graphic" in result ? result.graphic : result;

                if (
                  graphic?.attributes?.uuid ===
                  phaseTerritory.graphic?.attributes?.uuid
                ) {
                  dispatch(setPhaseTerritoryGraphic(null));
                }
              }

              if (event.key === "Escape") {
                clearLastSelectedGraphic([
                  tempLayer,
                  phaseTerritoryLayer,
                  polygonsLayer,
                  linesLayer,
                ]);
                return;
              }
            });

            await waitForViewerReady(viewer);

            dispatch(setMapComponentReady(section));

            updateSnappingWrapper();
          }
          return;
        } else if (viewer !== null) {
          viewer.map?.removeAll();
          viewer?.destroy();
          viewer = null;
          dispatch(setMapComponentReady(MapSection.none));
        }
      }

      if (viewer === null || store.getState().map.section !== section) {
        return next(action);
      }

      if (setLayersVisible.match(action)) {
        next(action);
        const { layers, visibleLayersId } = store.getState().map;
        layers
          .filter((layer) => masterCreateStudyTypes.includes(layer.dataType))
          .forEach((layer) => {
            if (layer.dataType === LayersDataTypes.BaseMap) {
              if (visibleLayersId.includes(layer.id))
                changeBaseMap(layer, viewer, getBaseMap);
            } else if (validArcgisLayerTypes.includes(layer.type)) {
              let mapLayers = viewer!.map.layers.filter(
                (l) => l.id === layer.name,
              );
              if (mapLayers) {
                const alwaysVisibleLayer =
                  layer.dataType === LayersDataTypes.OmanBorder;
                const visible =
                  visibleLayersId.includes(layer.id) ||
                  (!!layer.parentId &&
                    visibleLayersId.includes(layer.parentId)) ||
                  alwaysVisibleLayer;
                Promise.all(
                  mapLayers.map((mapLayer) => {
                    mapLayer.visible = visible;
                    return mapLayer.load();
                  }),
                ).finally(() => {
                  dispatch(setLoading({ id: layer.id, loading: false }));
                });
              }
            }
          });
        return;
      }

      if (zoomIn.match(action)) {
        viewer.zoom += 1;
      }
      if (zoomOut.match(action)) {
        viewer.zoom -= 1;
      }

      if (getLayers.fulfilled.match(action)) {
        next(action);
        AddLayers(viewer);
        return;
      }

      if (setGeometries.match(action)) {
        next(action);
        DrawTempGeometry();
        return;
      }

      if (startDrawingTools.match(action)) {
        const allLayers = [
          tempLayer,
          phaseTerritoryLayer,
          polygonsLayer,
          linesLayer,
        ];

        allLayers.forEach((layer) => {
          layer.graphics.forEach((graphic) => {
            DrawVertices(
              graphic.geometry as Polygon,
              graphic.attributes.uuid,
              false,
              verticesTempLayer,
            );
            DrawVerticesLabels(
              graphic.geometry as Polygon,
              graphic.attributes.uuid,
              false,
              verticesLabelTempLayer,
            );
          });
        });
      }

      if (stopDrawingTools.match(action)) {
        if (
          sketchViewModel.state === "active" &&
          sketchViewModel.createGraphic
        ) {
          EndCreateGraphic(sketchViewModel);
        } else {
          sketchViewModel.complete();
        }
        const allUuids = [
          ...tempLayer.graphics.map((g) => g.attributes.uuid),
          ...phaseTerritoryLayer.graphics.map((g) => g.attributes.uuid),
          ...polygonsLayer.graphics.map((g) => g.attributes.uuid),
          ...linesLayer.graphics.map((g) => g.attributes.uuid),
        ];

        verticesTempLayer.removeMany(
          verticesTempLayer.graphics
            .filter((g) => allUuids.includes(g.attributes.uuid))
            .toArray(),
        );

        verticesLabelTempLayer.removeMany(
          verticesLabelTempLayer.graphics
            .filter((g) => allUuids.includes(g.attributes.uuid))
            .toArray(),
        );
      }

      if (getGeometryForContour.match(action)) {
        const { plotIdx, contourIdx } = action.payload;
        const coordinatesData = store.getState().studyCreation.coordinatesData;
        const existingObject =
          store.getState().studyCreation.createdObject?.[0];
        const targetWkid = existingObject?.wkid;
        const result = await getGeometry(
          plotIdx,
          contourIdx,
          targetWkid,
          tempLayer,
          coordinatesData,
        );

        if (!result) return;

        const { uuid, updatedData } = result;

        [tempLayer, verticesTempLayer, verticesLabelTempLayer].forEach(
          (layer) => {
            const toRemove = layer.graphics
              .filter((g) => g.attributes?.uuid === uuid)
              .toArray();
            layer.graphics.removeMany(toRemove);
          },
        );

        dispatch(
          setGeometries({
            newGeometry: ConvertToPolygons(updatedData),
            stringGeometry: ConvertPolygonsToCoordinatesTable(updatedData),
          }),
        );

        dispatch(setSelectedFeature(null));
      }

      if (resetCreatedObject.match(action)) {
        deleteCreatedObjects(
          tempLayer,
          phaseTerritoryLayer,
          verticesTempLayer,
          verticesLabelTempLayer,
          polygonsLayer,
          linesLayer,
        );
        return next(action);
      }

      if (takeScreenshotAsyncAction.match(action)) {
        const { data, deferred } = action.payload;
        tempLayer.removeAll();
        DrawTempGeometry(true, false);

        try {
          const payloadWithLayers = data.map((params, index) => {
            const isLast = index === data.length - 1;

            return {
              ...params,
              graphicsLayers: [
                polygonsLayer,
                ...(isLast ? [phaseTerritoryLayer] : []),
                verticesTempLayer,
                verticesLabelTempLayer,
              ],
            };
          });

          const dataUrls = await createMultipleScreenshots(
            viewer,
            payloadWithLayers,
            getBaseMap,
            true,
            true,
          );

          deferred.resolve(
            dataUrls.map((result, index) => ({
              name: (index + 1).toString(),
              url: result.dataUrl,
            })),
          );
        } catch (err) {
          deferred.reject(err);
        }
      }

      if (drawBufferDistance.match(action)) {
        const state = store.getState();
        const bufferDistanceStr = state.studyCreation.mainForm.buffer;
        const createdObjectGeometry =
          state.studyCreation.createdObject[0].newGeometry;
        const bufferDistance = parseFloat(bufferDistanceStr);

        if (!createdObjectGeometry || isNaN(bufferDistance)) {
          return;
        }

        const rawBuffer = geometryEngine.buffer(
          createdObjectGeometry,
          bufferDistance,
          "meters",
        );

        const buffer = Array.isArray(rawBuffer) ? rawBuffer[0] : rawBuffer;

        const outline = new SimpleLineSymbol({
          style: "dash",
          color: [255, 255, 255],
          width: 2,
        });

        const fillSymbol = new SimpleFillSymbol({
          style: "none",
          outline,
        });

        const bufferGraphic = new Graphic({
          geometry: buffer,
          symbol: fillSymbol,
        });

        bufferLayer.removeAll();
        bufferLayer.add(bufferGraphic);
      }

      if (deleteBufferDistance.match(action)) {
        bufferLayer.removeAll();
      }

      if (setObjectVisibility.match(action)) {
        const { objectId, visible } = action.payload;

        const targetGraphic = phaseTerritoryLayer.graphics.find((g) => {
          const graphicId = g.attributes?.uuid;
          return graphicId === objectId;
        });

        if (targetGraphic) {
          targetGraphic.visible = visible;
        }

        verticesTempLayer.graphics
          .filter((g) => g.attributes.uuid === objectId)
          .forEach((vertex) => (vertex.visible = visible));

        verticesLabelTempLayer.graphics
          .filter((g) => g.attributes.uuid === objectId)
          .forEach((label) => (label.visible = visible));

        dispatch(setPhaseTerritoryVisible(visible));

        return;
      }

      if (removeTerritoryObject.match(action)) {
        const { objectId } = action.payload;

        const targetGraphic = phaseTerritoryLayer.graphics.find((g) => {
          const id = g.attributes?.uuid;
          return id === objectId;
        });

        if (targetGraphic) {
          phaseTerritoryLayer.remove(targetGraphic);
        }

        const vertices = verticesTempLayer.graphics.filter(
          (g) => g.attributes.uuid === objectId,
        );
        verticesTempLayer.removeMany(vertices.toArray());

        const labels = verticesLabelTempLayer.graphics.filter(
          (g) => g.attributes.uuid === objectId,
        );
        verticesLabelTempLayer.removeMany(labels.toArray());

        return;
      }

      if (setPhaseTerritoryGraphic.match(action)) {
        const graphic = action.payload;

        phaseTerritoryLayer.removeAll();

        if (graphic) {
          const uuid = graphic.attributes?.uuid;
          if (uuid) {
            const fromTemp = tempLayer.graphics.find(
              (g) => g.attributes?.uuid === uuid,
            );
            if (fromTemp) {
              tempLayer.remove(fromTemp);
            }
          }

          graphic.symbol = new SimpleFillSymbol({
            color: "#B04A5A80",
            outline: {
              color: "#B04A5A",
              width: 2,
            },
          });

          phaseTerritoryLayer.add(graphic);
          clearLastSelectedGraphic([
            tempLayer,
            phaseTerritoryLayer,
            polygonsLayer,
            linesLayer,
          ]);
        }
      }

      if (checkInfrastructureAvailability.match(action)) {
        const { resolve } = action.payload;

        try {
          const layersConfig = store.getState().map.layers;
          const coordObjectGeometry = store.getState().studyCreation
            .createdObject[0].newGeometry as Geometry;
          const bufferGraphic = bufferLayer.graphics.at(0);
          if (!bufferGraphic) return;
          const bufferGeometry: Geometry = bufferGraphic.geometry;

          await projection.load();

          const infrastructureChecks: Array<{
            dataType: LayersDataTypes;
            resultKey: keyof BufferInfrastructureResults;
          }> = [
            {
              dataType: LayersDataTypes.ElectricityInfrastructure,
              resultKey: "powerSupply",
            },
            {
              dataType: LayersDataTypes.WaterInfrastructure,
              resultKey: "water",
            },
            {
              dataType: LayersDataTypes.TelecomInfrastructure,
              resultKey: "telecom",
            },
            {
              dataType: LayersDataTypes.SewerageInfrastructure,
              resultKey: "drainage",
            },
            { dataType: LayersDataTypes.GasInfrastructure, resultKey: "gas" },
          ];

          dispatch(resetAllAnalysisResults());

          const bufferInfrastructureResults = {
            ...store.getState().studyCreation.bufferInfrastructureResults,
          };
          for (const { dataType, resultKey } of infrastructureChecks) {
            bufferInfrastructureResults[resultKey] =
              await getInfrastructureAvailability(
                layersConfig,
                dataType,
                coordObjectGeometry,
                bufferGeometry,
                viewer,
              );
          }

          dispatch(setBufferInfrastructureResults(bufferInfrastructureResults));

          const layersToCheck = layersConfig.filter((layer) =>
            [LayersDataTypes.OPIS, LayersDataTypes.NCSI].includes(
              layer.dataType,
            ),
          );

          let opisHasData = false;
          let ncsiHasData = false;

          for (const layer of layersToCheck) {
            const hasData = await checkLayerHasFeatures(viewer, layer);

            if (layer.dataType === LayersDataTypes.OPIS)
              opisHasData ||= hasData;
            if (layer.dataType === LayersDataTypes.NCSI)
              ncsiHasData ||= hasData;
          }

          dispatch(
            setExternalServicesAvailability({
              opis: opisHasData,
              ncsi: ncsiHasData,
            }),
          );

          const logisticsInfo = await getLogisticsDistances(
            layersConfig,
            viewer,
            coordObjectGeometry,
          );

          dispatch(setLogisticsResults(logisticsInfo));

          const createdObjectInfrastructureResults =
            await getDistanceAndDirectionToInfrastructure(
              viewer,
              coordObjectGeometry,
              layersConfig,
            );

          dispatch(
            setCreatedObjectInfrastructureResults(
              createdObjectInfrastructureResults,
            ),
          );
        } finally {
          resolve();
        }
      }

      if (drawVertices.match(action)) {
        verticesTempLayer.removeAll();
        verticesLabelTempLayer.removeAll();

        const allLayers = [polygonsLayer, linesLayer];

        allLayers.forEach((layer) => {
          layer.graphics.forEach((graphic) => {
            const geometry = graphic.geometry;
            const uuid = graphic.attributes?.uuid;

            if (geometry && uuid) {
              DrawVertices(geometry as Polygon, uuid, false, verticesTempLayer);
              DrawVerticesLabels(
                geometry as Polygon,
                uuid,
                false,
                verticesLabelTempLayer,
              );
            }
          });
        });

        return;
      }

      if (polylineOff.match(action)) {
        finishDrawingProcess(viewer);
        stopMeasuring(viewer);
      }
      if (polylineOn.match(action)) {
        startDrawingProcess(viewer);
      }

      next(action);
    };
  };
};
